import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown } from "@/components/ui/icons";
import { FunnelProgress } from "@/components/funnel-progress";
import { AccountCard, type AccountCardData } from "@/components/account-card";
import type { AccountGroup, GroupableAccount } from "@/lib/account-groups";
import { cn } from "@/lib/utils";

/**
 * One block per challenge-type category. Inside, phase subgroups are
 * rendered left-to-right in the order the category defines (e.g.
 * "Step 1 → Step 2 → Funded" for a 2-step firm), each as a responsive
 * grid. Archive is a collapsible row at the bottom — closed by
 * default so the operator's eye lands on active accounts first.
 *
 * The FunnelProgress at the top is a passive summary of *where the
 * accounts collectively sit*: the furthest phase with any active
 * account highlights as the current step. Lets the operator
 * eyeball the portfolio's progression at a glance.
 */
export function ChallengeGroupSection<
  T extends GroupableAccount & { data: AccountCardData },
>({
  group,
}: {
  group: AccountGroup<T>;
}): ReactNode {
  const [expanded, setExpanded] = useState(group.totals.active > 0);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Compute which phase dot to highlight on the FunnelProgress. Pick
  // the furthest phase that has any active account; fall back to 1.
  const furthestIdx = computeFurthestPhaseIndex(group);
  const phaseLabels = group.phases.map((p) => p.label);

  return (
    <section className="overflow-hidden rounded-xl border border-border/40 bg-muted/5">
      {/* ── Header ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/10 sm:px-5 sm:py-4"
      >
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-foreground">
            {group.label}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {group.totals.active} Active
            {group.totals.archived > 0 && ` · ${group.totals.archived} Archived`}
          </p>
        </div>
        <motion.span
          animate={{ rotate: expanded ? 0 : -90 }}
          transition={{ duration: 0.2 }}
          className="text-muted-foreground"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-5 border-t border-border/40 px-4 py-4 sm:px-5 sm:py-5">
              {/* Funnel summary — skip for "Other" (single bucket) */}
              {group.phases.length > 1 && (
                <FunnelProgress
                  steps={phaseLabels}
                  currentStep={furthestIdx}
                />
              )}

              {/* Phase subgroups */}
              {group.phases.map((phase) => {
                const rows = group.active[phase.key] ?? [];
                return (
                  <div key={phase.key} className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {phase.label}
                      </span>
                      <span className="font-mono text-[11px] text-muted-foreground/70">
                        {rows.length}
                      </span>
                    </div>
                    {rows.length > 0 ? (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {rows.map((r) => (
                          <AccountCard key={r.data.id} data={r.data} />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border/40 bg-muted/5 px-4 py-6 text-center text-[11px] text-muted-foreground/60">
                        No accounts in this phase
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Archive */}
              {group.archived.length > 0 && (
                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setArchiveOpen((v) => !v)}
                    className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border/50 bg-muted/10 px-3 py-2 text-left transition-colors hover:bg-muted/20"
                  >
                    <motion.span
                      animate={{ rotate: archiveOpen ? 0 : -90 }}
                      transition={{ duration: 0.2 }}
                      className="text-muted-foreground"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </motion.span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Archive
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground/70">
                      {group.archived.length}
                    </span>
                    <span className="ml-auto text-[11px] text-muted-foreground/60">
                      {archiveOpen ? "Hide" : "Show"}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {archiveOpen && (
                      <motion.div
                        key="archive"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <ArchivedOutcomeGrid group={group} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ArchivedOutcomeGrid<
  T extends GroupableAccount & { data: AccountCardData },
>({ group }: { group: AccountGroup<T> }): ReactNode {
  const passed = group.archived.filter((r) => r.outcome === "passed");
  const breached = group.archived.filter((r) => r.outcome === "breached");
  const passedLabel = group.category === "instant" ? "Payouts" : "Passed";

  return (
    <div className="space-y-4 pt-2">
      <ArchivedOutcomeSection label={passedLabel} rows={passed} />
      <ArchivedOutcomeSection label="Breached" rows={breached} />
    </div>
  );
}

function ArchivedOutcomeSection<
  T extends GroupableAccount & { data: AccountCardData },
>({ label, rows }: { label: string; rows: T[] }): ReactNode {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground/70">
          {rows.length}
        </span>
      </div>
      <div
        className={cn(
          "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        )}
      >
        {rows.map((r) => (
          <AccountCard key={r.data.id} data={r.data} muted />
        ))}
      </div>
    </div>
  );
}

/**
 * Highlight the furthest phase that has any active account, so
 * FunnelProgress reads as "your portfolio currently reaches X". Falls
 * back to step 1 when the category has no active rows (only archive).
 */
function computeFurthestPhaseIndex<T extends GroupableAccount>(
  group: AccountGroup<T>,
): number {
  let furthest = 0;
  group.phases.forEach((p, i) => {
    if ((group.active[p.key]?.length ?? 0) > 0) furthest = i + 1;
  });
  // Send `steps.length + 1` when the funded phase is populated so all
  // dots render complete — matches the old BatchFunnelCard behavior.
  const funded = group.phases[group.phases.length - 1];
  if (furthest === group.phases.length && funded?.key === "funded") {
    return group.phases.length + 1;
  }
  return furthest > 0 ? furthest : 1;
}
